import { lazy } from "react";

const CHUNK_RELOAD_KEY = "erp_chunk_reload_at";
const CHUNK_RELOAD_COOLDOWN_MS = 15000;

export function isChunkLoadError(error) {
  const message = String(error?.message || error || "");
  const name = String(error?.name || "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
}

/**
 * Reload once when a lazy chunk is missing (stale main.js after deploy).
 * Prevents infinite reload loops via sessionStorage cooldown.
 */
export function reloadForStaleChunk(error) {
  if (!isChunkLoadError(error)) return false;

  const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
  if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return false;

  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
  return true;
}

export function registerChunkLoadRecovery() {
  if (typeof window === "undefined") return;

  window.addEventListener("error", (event) => {
    const filename = String(event?.filename || "");
    const message = String(event?.message || "");
    const looksLikeStaleChunk =
      filename.includes(".chunk.js") && message.includes("Unexpected token");
    if (looksLikeStaleChunk || isChunkLoadError({ message })) {
      reloadForStaleChunk({ message, name: "ChunkLoadError" });
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (reloadForStaleChunk(event.reason)) {
      event.preventDefault();
    }
  });
}

/**
 * React.lazy wrapper — reloads page when chunk hash no longer matches deployed build.
 */
export function lazyWithRetry(factory) {
  return lazy(() =>
    factory().catch((error) => {
      if (reloadForStaleChunk(error)) {
        return new Promise(() => {});
      }
      throw error;
    })
  );
}
